import { AdvancedSelect } from '../../../../../widgets/select';
import { Button } from '../../../../../widgets/buttons';
import { Checkbox } from '../../../../../widgets/checkbox';
import { Dialog, DialogWindow, QuestionDialog, useDialogManager } from '../../../../../widgets/dialog';
import { FormControl } from '../../../../../widgets/formControl';
import React, { FC, useCallback, useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import OverlayLoading from 'src/components/widgets/overlayLoading';
import config from 'src/shared/config';
import { T } from 'src/shared/helpers';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import Stack from '../../../../../widgets/stack/stack';
import { ValidationBox } from '../../../../../widgets/validation/validationBox';

const Container = styled.div`
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: relative;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const Row = styled.div<{ $indented?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    margin-left: ${(props) => (props.$indented ? '24px' : '0')};
`;

const Label = styled.label`
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
`;

const StyledButton = styled(Button)`
    margin-left: 24px;
    margin-top: 8px;
`;

const IndentedSection = styled.div`
    padding-left: 24px;
`;

const DialogDecorator = styled(DialogWindow)`
    width: 50%;
    height: auto;
    min-height: 400px;
    display: flex;
    flex-direction: column;
    position: relative;
`;

const DialogContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
`;

const DialogButtonsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    justify-content: flex-end;
    margin-top: 20px;
`;

// Enum per i tipi di misurazione
enum SceneMeasureType {
    Width = 'width',
    Height = 'height',
    Depth = 'depth'
}

// Dialog per la misurazione della scena
const MeasureSceneDialog: FC<{
    sceneEditor: any;
    onConfirm: (meshId: string, dimension: SceneMeasureType, measure: number) => void;
}> = ({ sceneEditor, onConfirm }) => {
    const { closeDialog } = useDialogManager();
    const validationContext = useValidationContext();
    const [selectedMesh, setSelectedMesh] = useState<any>(null);
    const [dimension, setDimension] = useState<SceneMeasureType>(SceneMeasureType.Height);
    const [measure, setMeasure] = useState<string>('');

    // Ottieni le mesh disponibili
    const meshes = sceneEditor?.getSceneMeshes() || [];

    // Log dettagliato di ogni mesh disponibile
    meshes.forEach((mesh: any, index: number) => {});

    const meshOptions = meshes.map((mesh: any) => ({
        value: mesh.id,
        label: mesh.name || `Mesh ${mesh.id}`
    }));

    // Funzione per caricare le misure di una mesh
    const loadMeshMeasures = useCallback(
        (mesh: any) => {
            if (!mesh || !sceneEditor) return;

            const meshId = mesh.id;

            try {
                // Controlla tutti i metodi disponibili per le misurazioni

                const width = sceneEditor.getMeshWidthCm?.(meshId);
                const height = sceneEditor.getMeshHeightCm?.(meshId);
                const depth = sceneEditor.getMeshDepthCm?.(meshId);
                const generalSize = sceneEditor.getMeshSizeCm?.(meshId);

                if (width) {
                    setDimension(SceneMeasureType.Width);
                    setMeasure(width.toString());
                } else if (height) {
                    setDimension(SceneMeasureType.Height);
                    setMeasure(height.toString());
                } else if (depth) {
                    setDimension(SceneMeasureType.Depth);
                    setMeasure(depth.toString());
                } else {
                    // Nessuna misura esistente, resetta a valori default
                    setDimension(SceneMeasureType.Height);
                    setMeasure('');
                }
            } catch (error) {
                // Ignora errori sui metodi non disponibili
                setDimension(SceneMeasureType.Height);
                setMeasure('');
            }
        },
        [sceneEditor]
    );

    // Trova la mesh attualmente misurata - SOLO al primo caricamento
    useEffect(() => {
        if (sceneEditor && meshes.length > 0 && !selectedMesh) {
            // Trova la prima mesh che ha misure esistenti
            for (let mesh of meshes) {
                const meshId = mesh.id;
                try {
                    const width = sceneEditor.getMeshWidthCm?.(meshId);
                    const height = sceneEditor.getMeshHeightCm?.(meshId);
                    const depth = sceneEditor.getMeshDepthCm?.(meshId);

                    if (width || height || depth) {
                        setSelectedMesh(mesh);
                        loadMeshMeasures(mesh);
                        break;
                    }
                } catch (error) {
                    // Ignora errori sui metodi non disponibili
                }
            }
        }
    }, [sceneEditor, meshes, selectedMesh, loadMeshMeasures]); // Aggiunto loadMeshMeasures nelle dipendenze

    // Handler per il cambio di dimensione
    const handleDimensionChange = useCallback(
        (newDimension: SceneMeasureType) => {
            setDimension(newDimension);

            // Se c'Ã¨ una mesh selezionata, carica il valore per questa dimensione
            if (selectedMesh && sceneEditor) {
                const meshId = selectedMesh.id;
                try {
                    let currentValue = '';

                    switch (newDimension) {
                        case SceneMeasureType.Width:
                            currentValue = sceneEditor.getMeshWidthCm?.(meshId) || '';
                            break;
                        case SceneMeasureType.Height:
                            currentValue = sceneEditor.getMeshHeightCm?.(meshId) || '';
                            break;
                        case SceneMeasureType.Depth:
                            currentValue = sceneEditor.getMeshDepthCm?.(meshId) || '';
                            break;
                    }

                    setMeasure(currentValue.toString());
                } catch (error) {
                    setMeasure('');
                }
            }
        },
        [selectedMesh, sceneEditor]
    );

    const handleConfirm = () => {
        if (!validationContext.submit()) return;

        const meshId = selectedMesh.id;
        onConfirm(meshId, dimension, parseFloat(measure));
        closeDialog('measure-scene-dialog');
    };

    return (
        <Dialog windowDecorator={DialogDecorator} onClose={() => closeDialog('measure-scene-dialog')}>
            <DialogContent>
                <h3>{T._('Measure scene', 'Composer')}</h3>
                <p>{T._('To measure the scene, please select an object of which you know the size and type one of its dimensions.', 'Composer')}</p>

                <FormControl label={T._('Object', 'Composer')}>
                    <AdvancedSelect
                        options={meshOptions}
                        value={selectedMesh ? { value: selectedMesh.id, label: selectedMesh.name || `Mesh ${selectedMesh.id}` } : null}
                        onChange={(option: any) => {
                            const mesh = meshes.find((m: any) => m.id === option?.value);
                            setSelectedMesh(mesh || null);

                            // Carica le misure della mesh selezionata
                            if (mesh) {
                                loadMeshMeasures(mesh);
                            } else {
                                setMeasure('');
                                setDimension(SceneMeasureType.Height);
                            }
                        }}
                        placeholder={T._('Select an object', 'Composer')}
                        validationKey='measureSceneObject'
                        validationLabel={T._('Object', 'Composer')}
                        validationValue={selectedMesh}
                        validate={(value) => value != null}
                        validationMessage={T._('Object is required.', 'Composer')}
                    />
                </FormControl>

                {selectedMesh && (
                    <>
                        <Stack>
                            <FormControl label={T._('Known dimension', 'Composer')}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center' }}>
                                        <input
                                            type='radio'
                                            value={SceneMeasureType.Height}
                                            checked={dimension === SceneMeasureType.Height}
                                            onChange={(e) => handleDimensionChange(e.target.value as SceneMeasureType)}
                                            style={{ marginRight: '8px' }}
                                        />
                                        {T._('Height', 'Composer')}
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center' }}>
                                        <input
                                            type='radio'
                                            value={SceneMeasureType.Width}
                                            checked={dimension === SceneMeasureType.Width}
                                            onChange={(e) => handleDimensionChange(e.target.value as SceneMeasureType)}
                                            style={{ marginRight: '8px' }}
                                        />
                                        {T._('Width', 'Composer')}
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center' }}>
                                        <input
                                            type='radio'
                                            value={SceneMeasureType.Depth}
                                            checked={dimension === SceneMeasureType.Depth}
                                            onChange={(e) => handleDimensionChange(e.target.value as SceneMeasureType)}
                                            style={{ marginRight: '8px' }}
                                        />
                                        {T._('Depth', 'Composer')}
                                    </label>
                                </div>
                            </FormControl>

                            <FormControl label={T._('Measure (cm)', 'Composer')}>
                                <ValidationBox
                                    fullWidth
                                    validationKey='measureSceneValue'
                                    validationLabel={T._('Measure', 'Composer')}
                                    validationValue={measure}
                                    validate={(value) => validateRequiredTrimmed(value, false) || isNaN(parseFloat(value)) || parseFloat(value) <= 0}
                                    validationMessage={T._('Measure must be greater than 0.', 'Composer')}
                                >
                                    <input
                                        type='number'
                                        value={measure}
                                        onChange={(e) => setMeasure(e.target.value)}
                                        style={{
                                            padding: '8px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                        placeholder='0'
                                    />
                                </ValidationBox>
                            </FormControl>
                        </Stack>
                    </>
                )}

                <DialogButtonsContainer>
                    <Button onClick={() => closeDialog('measure-scene-dialog')}>{T._('Cancel', 'Composer')}</Button>
                    <Button onClick={handleConfirm}>{T._('Confirm', 'Composer')}</Button>
                </DialogButtonsContainer>
            </DialogContent>
        </Dialog>
    );
};

// Dialog per il QR Code AR Preview
const QRCodeDialog: FC<{ url: string }> = ({ url }) => {
    const { closeDialog } = useDialogManager();

    return (
        <Dialog onClose={() => closeDialog('qr-code-dialog')}>
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <h3>{T._('AR Preview Ready', 'Composer')}</h3>
                <p>{T._('Scan the QR code on your iPhone to preview in AR:', 'Composer')}</p>
                <div style={{ margin: '20px 0', display: 'flex', justifyContent: 'center' }}>
                    <QRCode value={url} size={256} />
                </div>
                {/* <div style={{ margin: '20px 0', fontSize: '12px', wordBreak: 'break-all', color: '#666' }}>{url}</div> */}
                <Button onClick={() => closeDialog('qr-code-dialog')}>{T._('Close', 'Composer')}</Button>
            </div>
        </Dialog>
    );
};

interface AREditorProps {
    sceneEditor?: any;
    zkScene?: any;
}

const AREditor: FC<AREditorProps> = ({ sceneEditor, zkScene }) => {
    const { showDialog, closeDialog } = useDialogManager();

    // Stati AR
    const [arEnabled, setArEnabled] = useState(false);
    const [arOnTheFly, setArOnTheFly] = useState(false);
    const [arAnchor, setArAnchor] = useState<'wall' | 'floor' | null>(null);
    const [arScaleEnabled, setArScaleEnabled] = useState(false);
    const [meshes, setMeshes] = useState<any[]>([]);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

    // Carica i dati iniziali
    useEffect(() => {
        if (sceneEditor && zkScene) {
            // Carica le mesh
            const sceneMeshes = sceneEditor.getSceneMeshes() || [];
            setMeshes(sceneMeshes);

            // Carica le impostazioni AR dal zkScene e sceneConfig
            try {
                const sceneConfig = sceneEditor.getSceneConfig();
                const generalConfig = sceneConfig?.get?.('general');

                if (generalConfig) {
                    setArEnabled(generalConfig.get('arEnabled') || false);
                    setArOnTheFly(generalConfig.get('arOnTheFly') || false);
                    setArAnchor(generalConfig.get('arAnchor') || null);
                    setArScaleEnabled(generalConfig.get('arScale') || false);
                }
            } catch (error) {}
        }
    }, [sceneEditor, zkScene]);

    // Controlla mesh con scaling negativo
    const checkMeshWithNegativeScaling = useCallback(() => {
        let meshesWithNegativeScaling: any[] = [];

        const hasGltfTransformNodeParent = (node: any): boolean => {
            if (node.metadata && node.metadata.isGltfTransformNode) {
                return true;
            }
            return false;
        };

        const checkMeshRecursive = (mesh: any) => {
            if (
                !(mesh.metadata || (mesh.metadata && mesh.metadata.isGltfTransformNode)) &&
                mesh.scaling &&
                (mesh.scaling.x < 0 || mesh.scaling.y < 0 || mesh.scaling.z < 0) &&
                mesh.parent &&
                !hasGltfTransformNodeParent(mesh.parent)
            ) {
                meshesWithNegativeScaling.push(mesh);
            }
            if (mesh.getChildMeshes) {
                mesh.getChildMeshes().forEach((child: any) => {
                    checkMeshRecursive(child);
                });
            }
        };

        meshes.forEach((mesh: any) => {
            checkMeshRecursive(mesh);
        });

        return meshesWithNegativeScaling;
    }, [meshes]);

    // Handler per l'abilitazione AR
    const handleArEnabledChange = useCallback(() => {
        const meshesWithNegativeScaling = checkMeshWithNegativeScaling();

        if (meshesWithNegativeScaling.length > 0) {
            const meshNames = meshesWithNegativeScaling.map((mesh: any) => mesh.name).join(', ');

            showDialog(
                'ar-warning-dialog',
                <Dialog onClose={() => closeDialog('ar-warning-dialog')}>
                    <QuestionDialog
                        title={T._('Removing a transform node', 'Composer')}
                        message={T._(
                            `You have "${meshNames}" node(s) without transform folder as parent, this could cause issues while using the AR mode, do you still want to continue?`,
                            'Composer'
                        )}
                        onYesClick={() => {
                            closeDialog('ar-warning-dialog');
                            toggleArEnabled();
                        }}
                        onNoClick={() => closeDialog('ar-warning-dialog')}
                    />
                </Dialog>
            );
        } else {
            toggleArEnabled();
        }
    }, [checkMeshWithNegativeScaling, showDialog, closeDialog]);

    const toggleArEnabled = useCallback(() => {
        const newValue = !arEnabled;
        setArEnabled(newValue);

        // Salva nel sceneConfig seguendo il pattern corretto
        try {
            if (sceneEditor) {
                const sceneConfig = sceneEditor.getSceneConfig();
                const generalConfig = sceneConfig?.get?.('general');
                if (generalConfig) {
                    generalConfig.set('arEnabled', newValue);
                }
            }
        } catch (error) {
            console.error('Error saving AR enabled config:', error);
        }
    }, [arEnabled, sceneEditor]);

    // Handler per altre impostazioni AR
    const handleArOnTheFlyChange = useCallback(() => {
        const newValue = !arOnTheFly;
        setArOnTheFly(newValue);

        try {
            if (sceneEditor) {
                const sceneConfig = sceneEditor.getSceneConfig();
                const generalConfig = sceneConfig?.get?.('general');
                if (generalConfig) {
                    generalConfig.set('arOnTheFly', newValue);
                }
            }
        } catch (error) {
            console.error('Error saving AR onTheFly config:', error);
        }
    }, [arOnTheFly, sceneEditor]);

    const handleArScaleEnabledChange = useCallback(() => {
        const newValue = !arScaleEnabled;
        setArScaleEnabled(newValue);

        try {
            if (sceneEditor) {
                const sceneConfig = sceneEditor.getSceneConfig();
                const generalConfig = sceneConfig?.get?.('general');
                if (generalConfig) {
                    generalConfig.set('arScale', newValue);
                }
            }
        } catch (error) {
            console.error('Error saving AR scale config:', error);
        }
    }, [arScaleEnabled, sceneEditor]);

    const handleArAnchorChange = useCallback(
        (anchor: 'wall' | 'floor' | null) => {
            setArAnchor(anchor);

            try {
                if (sceneEditor) {
                    const sceneConfig = sceneEditor.getSceneConfig();
                    const generalConfig = sceneConfig?.get?.('general');
                    if (generalConfig) {
                        generalConfig.set('arAnchor', anchor);
                    }
                }
            } catch (error) {
                console.error('Error saving AR anchor config:', error);
            }
        },
        [sceneEditor]
    );

    // Handler per generare AR Preview
    const handleGeneratePreview = useCallback(async () => {
        if (!sceneEditor) {
            console.warn('âš ï¸ SceneEditor not available');
            return;
        }

        setIsGeneratingPreview(true);
        try {
            // Verifica che Resumable sia disponibile globalmente
            if (typeof (window as any).Resumable === 'undefined') {
                console.error('âŒ Resumable not found on window object');
                throw new Error('Resumable library not available globally');
            }

            // Verifica che il token sia disponibile nel config locale
            if (!config.token) {
                console.error('âŒ No token available in local config');
                throw new Error('Authentication token not available');
            }

            if (typeof sceneEditor.exportSceneToUsdzAsync === 'function') {
                const url = await sceneEditor.exportSceneToUsdzAsync();

                if (url) {
                    showDialog('qr-code-dialog', <QRCodeDialog url={url} />);
                } else {
                    console.warn('âš ï¸ No URL returned from exportSceneToUsdzAsync');
                }
            } else {
                console.warn('âš ï¸ exportSceneToUsdzAsync method not available');
            }
        } catch (error) {
            console.error('âŒ Error generating AR preview:', error);
        } finally {
            setIsGeneratingPreview(false);
        }
    }, [sceneEditor, showDialog]);

    // Misura scena per AR
    const handleMeasureSceneClick = useCallback(() => {
        if (!sceneEditor) {
            console.warn('âš ï¸ SceneEditor not available');
            return;
        }

        const handleConfirm = (meshId: string, measureType: SceneMeasureType, measure: number) => {
            try {
                // Controlla i metodi disponibili

                // Imposta la dimensione della mesh seguendo il pattern originale
                if (typeof sceneEditor.setMeshSizeCm === 'function') {
                    sceneEditor.setMeshSizeCm(meshId, measure, measureType);
                } else {
                    console.warn('âš ï¸ setMeshSizeCm method not available');
                }

                const mesh = sceneEditor!.getMeshByID(meshId);

                // Verifica che la misurazione sia stata effettivamente impostata
                if (typeof sceneEditor.getMeshSizeCm === 'function') {
                    const newSize = sceneEditor.getMeshSizeCm(meshId);
                }
            } catch (error) {
                console.error('âŒ Error setting mesh size:', error);
            }
        };

        // Apri la dialog per la misurazione
        showDialog(
            'measure-scene-dialog',
            <ValidationStateProvider>
                <MeasureSceneDialog sceneEditor={sceneEditor} onConfirm={handleConfirm} />
            </ValidationStateProvider>
        );
    }, [sceneEditor, showDialog]);

    if (!sceneEditor) {
        return <div>Scene Editor not available</div>;
    }

    return (
        <Container>
            {isGeneratingPreview && <OverlayLoading forceShow />}

            <Section>
                <FormControl>
                    <Checkbox label={T._('Augmented Reality', 'Composer')} checked={arEnabled} onChange={handleArEnabledChange} helpTooltipPosition='bottom' />
                </FormControl>
            </Section>

            {arEnabled && (
                <IndentedSection>
                    <Section>
                        <FormControl>
                            <Checkbox
                                label={T._('Make product resizable in AR', 'Composer')}
                                checked={arScaleEnabled}
                                onChange={handleArScaleEnabledChange}
                                helpTooltip={T._(
                                    "By enabling this option, your customers will be able to resize the customized item while they see it with the AR. If you don't want them to be able to change the size of the item when they use the AR, disable it.",
                                    'Composer'
                                )}
                                helpTooltipPosition='bottom'
                            />
                        </FormControl>

                        <FormControl>
                            <Checkbox
                                label={T._('Faster AR Conversion (Client-side)', 'Composer')}
                                checked={arOnTheFly}
                                onChange={handleArOnTheFlyChange}
                                helpTooltip={T._(
                                    "âš ï¸ This feature is temporarily not correctly working for android devices.\n\nBy default, the AR system in Zakeke operates on the server side, providing enhanced accountability and reliability. However, if you experience slow AR loading times, you have the option to enable client-side conversion for faster performance. It's important to note that client-side conversion may encounter difficulties with more complex models or scenes, potentially resulting in broken models. The server-side option ensures a robust and dependable AR experience, while the client-side option offers faster loading times with some limitations. Also, be aware that client-side conversion only works with iOS devices.",
                                    'Composer'
                                )}
                                helpTooltipPosition='bottom'
                            />
                        </FormControl>

                        <FormControl>
                            <Checkbox
                                label={T._('Enable Wall Placement', 'Composer')}
                                checked={arAnchor === 'wall'}
                                onChange={(checked) => handleArAnchorChange(checked ? 'wall' : null)}
                                helpTooltip={T._(
                                    'Activate this feature to enable wall placement in the AR experience. For merchants selling products that are intended to be displayed on walls, such as canvases, this option allows the AR to recognize and position the product accurately on a wall.',
                                    'Composer'
                                )}
                                helpTooltipPosition='bottom'
                            />
                        </FormControl>

                        <Row $indented>
                            <StyledButton onClick={handleGeneratePreview} disabled={isGeneratingPreview}>
                                {isGeneratingPreview ? T._('Generating...', 'Composer') : T._('See in Preview', 'Composer')}
                            </StyledButton>
                        </Row>

                        <Row $indented>
                            <StyledButton onClick={handleMeasureSceneClick}>{T._('Measure scene for AR', 'Composer')}</StyledButton>
                        </Row>
                    </Section>
                </IndentedSection>
            )}
        </Container>
    );
};

export default AREditor;
